const RESULT_EVENT = 'alternativeBillingResult';
const START_MESSAGE = 'START_ALTERNATIVE_BILLING';
const ALTERNATIVE_BILLING_TIMEOUT_MS = 60_000;

const ALTERNATIVE_BILLING_FAILURE_REASONS = [
  'ALREADY_IN_PROGRESS',
  'BILLING_UNAVAILABLE',
  'DEVELOPER_ERROR',
  'FEATURE_NOT_SUPPORTED',
  'INTERNAL_ERROR',
  'ITEM_ALREADY_OWNED',
  'ITEM_NOT_OWNED',
  'ITEM_UNAVAILABLE',
  'NETWORK_ERROR',
  'SERVICE_DISCONNECTED',
  'SERVICE_TIMEOUT',
  'SERVICE_UNAVAILABLE',
  'UNKNOWN_RESPONSE_CODE',
  'USER_CANCELED',
] as const;

type AlternativeBillingFailureReason = (typeof ALTERNATIVE_BILLING_FAILURE_REASONS)[number];

export type AlternativeBillingOutcome =
  | { status: 'unavailable' }
  | { status: 'success'; externalTransactionToken: string }
  | { status: 'canceled' }
  | { status: 'aborted' }
  | { status: 'failure'; reason: Exclude<AlternativeBillingFailureReason, 'USER_CANCELED'> };

declare global {
  interface Window {
    alternativePaymentWebBridge?: {
      postMessage: (message: string) => void;
    };
  }
}

const createRequestId = () =>
  window.crypto?.randomUUID?.() ?? `alternative-billing-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isFailureReason = (value: unknown): value is AlternativeBillingFailureReason =>
  typeof value === 'string' && ALTERNATIVE_BILLING_FAILURE_REASONS.includes(value as AlternativeBillingFailureReason);

export const isAlternativeBillingBridgeAvailable = () =>
  typeof window.alternativePaymentWebBridge?.postMessage === 'function';

export const startAlternativeBilling = (signal?: AbortSignal): Promise<AlternativeBillingOutcome> => {
  if (signal?.aborted) {
    return Promise.resolve({ status: 'aborted' });
  }

  if (!isAlternativeBillingBridgeAvailable()) {
    return Promise.resolve({ status: 'unavailable' });
  }

  const bridge = window.alternativePaymentWebBridge!;
  const requestId = createRequestId();

  return new Promise((resolve) => {
    let settled = false;

    function cleanup() {
      window.removeEventListener(RESULT_EVENT, handleResult);
      signal?.removeEventListener('abort', handleAbort);
      window.clearTimeout(timeoutId);
    }

    function settle(outcome: AlternativeBillingOutcome) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(outcome);
    }

    function handleAbort() {
      settle({ status: 'aborted' });
    }

    function handleResult(event: Event) {
      const result = (event as CustomEvent<unknown>).detail;
      if (!isRecord(result) || result.requestId !== requestId) {
        return;
      }

      if (result.type !== 'ALTERNATIVE_BILLING_RESULT') {
        settle({ status: 'failure', reason: 'UNKNOWN_RESPONSE_CODE' });
        return;
      }

      if (result.status === 'success') {
        const token = result.externalTransactionToken;
        settle(
          typeof token === 'string' && token.trim()
            ? { status: 'success', externalTransactionToken: token }
            : { status: 'failure', reason: 'UNKNOWN_RESPONSE_CODE' },
        );
        return;
      }

      if (result.status === 'failure' && isFailureReason(result.reason)) {
        if (result.reason === 'USER_CANCELED') {
          settle({ status: 'canceled' });
        } else {
          settle({ status: 'failure', reason: result.reason });
        }
        return;
      }

      settle({ status: 'failure', reason: 'UNKNOWN_RESPONSE_CODE' });
    }

    const timeoutId = window.setTimeout(
      () => settle({ status: 'failure', reason: 'SERVICE_TIMEOUT' }),
      ALTERNATIVE_BILLING_TIMEOUT_MS,
    );
    window.addEventListener(RESULT_EVENT, handleResult);
    signal?.addEventListener('abort', handleAbort, { once: true });

    try {
      bridge.postMessage(JSON.stringify({ type: START_MESSAGE, requestId }));
    } catch {
      settle({ status: 'failure', reason: 'INTERNAL_ERROR' });
    }
  });
};
